import React from 'react';
import { Col, Form, InputNumber, Row } from 'antd';
import { useIntl } from 'umi';
import { F } from '@/utils/intl';
import BookingSettingsVisualization from './BookingSettingsVisualization';
import ToggleCard from '@/components/ToggleCard/ToggleCard';
import NavLabel from '@/components/NavLabel';
import styles from './index.less';

interface ServiceBookingSettingsProps {
  form: any;
  onPatch?: (values: any) => void;
  isServiceType?: boolean;
}

const ServiceBookingSettings: React.FC<ServiceBookingSettingsProps> = ({
  form,
  onPatch,
  isServiceType = false,
}) => {
  const intl = useIntl();

  const appointments_duration = Form.useWatch('appointments_duration', form);
  const appointments_buffer = Form.useWatch('appointments_buffer', form);
  const timeslot_increment = Form.useWatch('timeslot_increment', form);

  const fromFieldName = isServiceType ? 'appointments_from' : 'appointment_available_from';

  const handleBlur = (fieldName: string) => {
    if (onPatch) {
      onPatch({ [fieldName]: form.getFieldValue(fieldName) });
    }
  };

  return (
    <div className={styles.bookingSettingsContainer}>
      <NavLabel strong size={'large'} noPadding>
        <F
          id="pages.locations.appointmentSettings"
          defaultMessage="Booking Settings"
        />
      </NavLabel>
      <br />
      <Row gutter={16}>
        <Col md={8} sm={24}>
          <Form.Item
            layout={'vertical'}
            colon={false}
            className={'form-item-mini-margin'}
            label={
              <F
                id="pages.locations.appointment_duration"
                defaultMessage="Appointment Duration"
              />
            }
            name="appointments_duration"
          >
            <InputNumber
              min={1}
              variant={'underlined'}
              addonAfter={<F id="common.minutes" defaultMessage="min" />}
              onBlur={() => handleBlur('appointments_duration')}
            />
          </Form.Item>
        </Col>
        <Col md={8} sm={24}>
          <Form.Item
            layout={'vertical'}
            className={'form-item-mini-margin'}
            colon={false}
            label={
              <F
                id="pages.locations.appointment_buffer"
                defaultMessage="Buffer"
              />
            }
            variant={'underlined'}
            name="appointments_buffer"
            tooltip={intl.formatMessage({
              id: 'pages.locations.appointment_buffer_tooltip',
              defaultMessage: 'Time kept free between appointments in minutes',
            })}
          >
            <InputNumber
              min={0}
              variant={'underlined'}
              addonAfter={<F id="common.minutes" defaultMessage="min" />}
              onBlur={() => handleBlur('appointments_buffer')}
            />
          </Form.Item>
        </Col>
        <Col md={8} sm={24}>
          <Form.Item
            colon={false}
            layout={'vertical'}
            className={'form-item-mini-margin'}
            label={
              <F
                id="pages.locations.timeslot_increment"
                defaultMessage="Timeslot increments"
              />
            }
            name="timeslot_increment"
            tooltip={intl.formatMessage({
              id: 'pages.locations.timeslot_increment_tooltip',
              defaultMessage:
                'Frequency at which free timeslots are suggested in minutes',
            })}
          >
            <InputNumber
              min={1}
              variant={'underlined'}
              addonAfter={<F id="common.minutes" defaultMessage="min" />}
              onBlur={() => handleBlur('timeslot_increment')}
            />
          </Form.Item>
        </Col>
      </Row>

      <BookingSettingsVisualization
        duration={appointments_duration}
        buffer={appointments_buffer}
        increment={timeslot_increment}
      />

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            className={'form-item-mini-margin'}
            colon={false}
            layout={'vertical'}
            label={
              <F
                id="pages.locations.appointment_available_from"
                defaultMessage="Booking earliest in"
              />
            }
            name={fromFieldName}
            tooltip={intl.formatMessage({
              id: 'pages.locations.appointment_available_from_tooltip',
              defaultMessage:
                'Time in hours until the first appointment can be booked',
            })}
          >
            <InputNumber
              min={0}
              variant={'underlined'}
              addonAfter={
                <F id="pages.locations.hours" defaultMessage="hours" />
              }
              onBlur={() => handleBlur(fromFieldName)}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            layout={'vertical'}
            className={'form-item-mini-margin'}
            colon={false}
            label={
              <F
                id="pages.locations.appointment_available_to"
                defaultMessage="Appointments available until"
              />
            }
            name="appointments_until"
            tooltip={intl.formatMessage({
              id: 'pages.locations.appointment_available_to_tooltip',
              defaultMessage:
                'Maximum number of days an appointment can be booked in advance',
            })}
          >
            <InputNumber
              min={1}
              variant={'underlined'}
              addonAfter={<F id="common.days" defaultMessage="days" />}
              onBlur={() => handleBlur('appointments_until')}
            />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        style={{ marginTop: 10 }}
        className={'form-item-mini-margin'}
        name="with_video_call"
        valuePropName="checked"
      >
        <ToggleCard
          title={
            <F id="pages.location.videoCall" defaultMessage="Video Call" />
          }
          description={
            <F
              id="pages.locations.videoCall.description"
              defaultMessage="Allow customers to book video call appointments for this service"
            />
          }
          onChange={(checked) => onPatch?.({ with_video_call: checked })}
        />
      </Form.Item>
    </div>
  );
};

export default ServiceBookingSettings;
